import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

/**
 * EntityFormDialog — wrapper standard per tutti i form di creazione/modifica.
 * Gold standard: `Utenti.jsx`.
 *
 * Props:
 *  - open: bool
 *  - onOpenChange: (open) => void
 *  - title: string                (es. "Nuovo template")
 *  - description?: string         (subtitle sotto al titolo)
 *  - size?: "sm" | "md" | "lg" | "xl"  (default "md")
 *  - onSubmit: (e) => void        (handler del <form>)
 *  - submitLabel: string          (es. "Crea utente" o "Salva")
 *  - saving?: bool                (mostra spinner sul submit)
 *  - submitDisabled?: bool
 *  - testId?: string              (data-testid del form)
 *  - submitTestId?: string        (data-testid del submit button)
 *  - cancelLabel?: string         (default "Annulla")
 *  - scrollable?: bool            (aggiunge max-h-[85vh] overflow al body)
 *  - footerExtra?: ReactNode      (slot opzionale a sx del footer)
 *  - children: campi del form (inseriti dentro <form>)
 */
const SIZE_MAP = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-3xl",
};

export default function EntityFormDialog({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  onSubmit,
  submitLabel = "Salva",
  saving = false,
  submitDisabled = false,
  testId,
  submitTestId,
  cancelLabel = "Annulla",
  scrollable = false,
  footerExtra,
  children,
}) {
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`ih-glass-strong ${sizeClass} ${scrollable ? "max-h-[90vh] overflow-y-auto" : ""}`}
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid={testId}>
          {children}
          <DialogFooter className="gap-2 sm:gap-2">
            {footerExtra}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              {cancelLabel}
            </Button>
            <Button
              type="submit"
              disabled={saving || submitDisabled}
              data-testid={submitTestId}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
